/**
 * EU VAT number (vatVerified / Factur-X BT-48) validation, mirroring the back-end rule
 * validateVat(country_code, vat) from ISJ-9154. Format/structure only, no VIES lookup.
 */

const EU_VAT_PATTERNS = {
  AT: /^ATU[0-9]{8}$/,
  BE: /^BE[0-1][0-9]{9}$/,
  BG: /^BG[0-9]{9,10}$/,
  CY: /^CY[0-9]{8}[A-Z]$/,
  CZ: /^CZ[0-9]{8,10}$/,
  DE: /^DE[0-9]{9}$/,
  DK: /^DK[0-9]{8}$/,
  EE: /^EE[0-9]{9}$/,
  EL: /^EL[0-9]{9}$/,
  ES: /^ES[A-Z0-9][0-9]{7}[A-Z0-9]$/,
  FI: /^FI[0-9]{8}$/,
  FR: /^FR[A-Z0-9]{2}[0-9]{9}$/,
  HR: /^HR[0-9]{11}$/,
  HU: /^HU[0-9]{8}$/,
  IE: /^(IE[0-9]{7}[A-Z]{1,2}|IE[0-9][A-Z][0-9]{5}[A-Z])$/,
  IT: /^IT[0-9]{11}$/,
  LT: /^LT([0-9]{9}|[0-9]{12})$/,
  LU: /^LU[0-9]{8}$/,
  LV: /^LV[0-9]{11}$/,
  MT: /^MT[0-9]{8}$/,
  NL: /^NL[0-9]{9}B[0-9]{2}$/,
  PL: /^PL[0-9]{10}$/,
  PT: /^PT[0-9]{9}$/,
  RO: /^RO[0-9]{2,10}$/,
  SE: /^SE[0-9]{12}$/,
  SI: /^SI[0-9]{8}$/,
  SK: /^SK[0-9]{10}$/,
};

// Countries with no EU pattern still get a structural check, as the back end does.
export const WORLDWIDE_VAT_MAX_LENGTH = 20;
const WORLDWIDE_VAT_PATTERN = new RegExp(`^[A-Z0-9]{2,${WORLDWIDE_VAT_MAX_LENGTH}}$`);

const VAT_FORMAT_EXAMPLES = {
  AT: 'ATU12345678',
  BE: 'BE0123456789',
  BG: 'BG123456789',
  CY: 'CY12345678L',
  CZ: 'CZ12345678',
  DE: 'DE123456789',
  DK: 'DK12345678',
  EE: 'EE123456789',
  EL: 'EL123456789',
  ES: 'ESX1234567X',
  FI: 'FI12345678',
  FR: 'FR32123456789',
  HR: 'HR12345678901',
  HU: 'HU12345678',
  IE: 'IE1234567A',
  IT: 'IT12345678901',
  LT: 'LT123456789',
  LU: 'LU12345678',
  LV: 'LV12345678901',
  MT: 'MT12345678',
  NL: 'NL123456789B01',
  PL: 'PL1234567890',
  PT: 'PT123456789',
  RO: 'RO1234567890',
  SE: 'SE123456789012',
  SI: 'SI12345678',
  SK: 'SK1234567890',
};

/**
 * Sanitize a VAT number the same way the back-end does: trim, uppercase, strip spaces/dots/dashes.
 * @param {string} value raw input
 * @returns {string} sanitized value
 */
export const sanitizeVat = value =>
  String(value == null ? '' : value)
    .trim()
    .toUpperCase()
    .replace(/[\s.-]/g, '');

/**
 * Map a business country code to its VAT validation key (Greece GR uses the historical EL key).
 * @param {string} countryCode ISO 3166-1 alpha-2 country code
 * @returns {string} VAT validation key
 */
export const getVatValidationKey = countryCode => {
  const code = String(countryCode == null ? '' : countryCode)
    .trim()
    .toUpperCase();
  return code === 'GR' ? 'EL' : code;
};

/**
 * Validate a verified VAT number against the business country. Empty is allowed (optional field).
 * EU countries have a per-country format; any other country falls back to the worldwide rule of
 * 2 to 20 alphanumeric characters.
 * @param {string} countryCode business country
 * @param {string} rawVat raw VAT input
 * @returns {boolean} true when the value is acceptable to submit
 */
export const validateVerifiedVat = (countryCode, rawVat) => {
  const vat = sanitizeVat(rawVat);
  if (vat === '') return true;

  const pattern = EU_VAT_PATTERNS[getVatValidationKey(countryCode)] || WORLDWIDE_VAT_PATTERN;

  return pattern.test(vat);
};

/**
 * A canonical example VAT for the business country, for helper text. Returns null when the country
 * is missing or is not an EU country we validate.
 * @param {string} countryCode business country (address.countryCode)
 * @returns {string|null}
 */
export const getVatFormatExample = countryCode => VAT_FORMAT_EXAMPLES[getVatValidationKey(countryCode)] || null;
